import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import _without from 'lodash/without';
import _get from 'lodash/get';
import { translator, selectors as i18nSelectors } from 'mage-i18n';
import {
  ButtonPrimary,
  ButtonSecondary,
  SpacerHorizontal,
  SpacerVertical,
} from '@klarna/bubble-ui';
import {
  analytics,
  defaultContainerPropTypes,
  HorizontalWrapper,
  formUtils,
  FieldsetForObject,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { ContainerTemplate, AutoSuggest } from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';
import { getCountrySupportedPlatforms, getSelectedPlatform } from '@/selectors/platforms';
import { getSelectedPartner, getStandardPartners } from '@/selectors/partners';
import {
  getVolumeSeparationConfig,
  isKustomPartner,
  getSelectedCountryKey,
  getSelectedAnnualRevenue,
} from '@/selectors/data';
import { CAPS } from '@/constants';
import * as a from '@/actions/signup';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import {
  CY_VOLUME_SEPARATION_CONTAINER_IDS,
  CY_PLATFORM_CONTAINER_IDS,
  T_PREFIX,
} from './constants';

const tPrefix = 'products-fe.containers.volume_separation';

export const VolumeSeparation = ({
  currencyFormatterFactory,
  volumeSeparationConfig,
  language,
  partner,
  partners,
  platform,
  platforms,
  t,
  onNext,
  onBack,
  onSelectRevenue,
  onChangePlatform,
  onChangePartner,
  annualRevenue,
  isDisabled,
  isKustomPartner,
  country,
}) => {
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState(() => prepareFields());
  const brand = isKustomPartner ? 'Kustom' : 'Klarna';

  function prepareVolumeSeparationOptions() {
    const { currency, skipMedium, large = 0, medium = 0 } = volumeSeparationConfig;
    const currencyFormatter = currencyFormatterFactory(currency, true);

    const formatCurrency = value => {
      const formattedValue = currencyFormatter.format(value);
      return typeof formattedValue === 'string'
        ? formattedValue.replace('US$', '$')
        : formattedValue;
    };
    const smallOption = {
      // when `skipMedium` we treat merchant as small business merchant
      value: `${medium}`,
      label: t(`products-fe.containers.volume_separation.below`, {
        threshold: formatCurrency(skipMedium ? large : medium), // when `skipMedium`, we show below `large` and above `large` as options
      }),
      tier: 'small',
      ...generateCyElemId(CY_VOLUME_SEPARATION_CONTAINER_IDS.VOLUME('BELOW'), window),
    };
    const mediumOption = !skipMedium
      ? {
          value: `${(medium + large) / 2}`,
          label: t(`products-fe.containers.volume_separation.between`, {
            belowThreshold: formatCurrency(medium),
            aboveThreshold: formatCurrency(large),
          }),
          tier: 'medium',
          ...generateCyElemId(CY_VOLUME_SEPARATION_CONTAINER_IDS.VOLUME('BETWEEN'), window),
        }
      : null;
    const largeOption = {
      value: `${large}`,
      label: t(`products-fe.containers.volume_separation.over`, {
        threshold: formatCurrency(large),
      }),
      tier: 'large',
      ...generateCyElemId(CY_VOLUME_SEPARATION_CONTAINER_IDS.VOLUME('ABOVE'), window),
    };

    return _without([smallOption, mediumOption, largeOption], null);
  }

  function prepareFields() {
    // const partnerOptions = [
    //   ...partners
    //     .filter(partner => partner.supportedCountries.includes(country))
    //     .map(({ id, name }) => ({
    //       value: id,
    //       label: name,
    //     })),
    // ];

    const platformOptions = [
      ...platforms.map(({ id, name }) => ({
        value: id,
        label: id === 'custom' ? t(`${T_PREFIX}.custom`) : name,
        ...generateCyElemId(CY_PLATFORM_CONTAINER_IDS.PLATFORM(id), window),
      })),
    ];

    return formUtils.initForm({
      annualRevenue: {
        component: AutoSuggest,
        size: 1,
        label: t(`${tPrefix}.selector_label`),
        value: annualRevenue,
        extraProps: {
          borderless: true,
          options: prepareVolumeSeparationOptions(),
          noEmptyOption: true,
        },
      },
      platform: {
        component: AutoSuggest,
        size: 1,
        label: t(`${T_PREFIX}.platform_search.label`),
        value: _get(platform, 'name', ''),
        extraProps: {
          borderless: true,
          options: platformOptions,
          noEmptyOption: true,
        },
      },
      // ...(partnerOptions.length > 0
      //   ? {
      //     partner: {
      //       component: AutoSuggest,
      //       size: 1,
      //       label: t(`${tPrefix}.selector_partner_label`),
      //       value: _get(partner, 'name', ''),
      //       extraProps: {
      //         borderless: true,
      //         options: partnerOptions,
      //         noEmptyOption: true,
      //       },
      //     }
      //   } : {}
      // )
    });
  }

  const handleFieldChange = (key, value) => {
    formUtils.onChangeFields(key, value, { fields }, setFields);
    if (key === 'annualRevenue') {
      onSelectRevenue(value);
    } else if (key === 'platform') {
      onChangePlatform(value);
    } else if (key === 'partner') {
      onChangePartner(value);
    }
  };

  const handleOnNext = async () => {
    try {
      setLoading(true);
      onNext({
        payload: { params: { country, platform: platform.id } },
      }).catch(() => setLoading(false));
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContainerTemplate
      title={t(`${tPrefix}.title_line_1`) + ' ' + t(`${tPrefix}.title_line_2`)}
      subtitle={t(`${tPrefix}.subtitle`, { brand })}
      showRecaptchaFooter
    >
      <SpacerVertical spaceToken="space/150" />
      <FieldsetForObject
        name="store-details"
        isDisabled={isDisabled}
        fields={fields}
        onBlur={key => formUtils.onBlurFields(key, { fields }, handleFieldChange)}
        onChange={handleFieldChange}
      />
      <SpacerVertical spaceToken="space/600" />

      <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
        <ButtonSecondary
          onClick={onBack}
          disabled={isDisabled}
          {...generateCyElemId(CY_VOLUME_SEPARATION_CONTAINER_IDS.BTN_BACK, window)}
        >
          {t(`${tPrefix}.buttons.back`)}
        </ButtonSecondary>
        <SpacerHorizontal spaceToken="space/300" />
        <ButtonPrimary
          onClick={handleOnNext}
          loading={loading}
          {...generateCyElemId(CY_VOLUME_SEPARATION_CONTAINER_IDS.BTN_CONTINUE, window)}
        >
          {t(`${tPrefix}.buttons.continue`)}
        </ButtonPrimary>
      </HorizontalWrapper>
    </ContainerTemplate>
  );
};

VolumeSeparation.propTypes = {
  ...defaultContainerPropTypes,
  annualRevenue: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  partner: PropTypes.string.isRequired,
  partners: PropTypes.array.isRequired,
  platform: PropTypes.string.isRequired,
  platforms: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  onChangePartner: PropTypes.func.isRequired,
  onChangePlatform: PropTypes.func.isRequired,
  onSelectRevenue: PropTypes.func.isRequired,
  volumeSeparationConfig: PropTypes.shape({
    AO: PropTypes.arrayOf(PropTypes.string).isRequired,
    currency: PropTypes.string.isRequired,
    large: PropTypes.number.isRequired,
    medium: PropTypes.number.isRequired,
    isKustomPartner: PropTypes.bool,
    skipMedium: PropTypes.bool,
  }),
};

export default compose(
  connect(
    state => ({
      volumeSeparationConfig: getVolumeSeparationConfig(state),
      currencyFormatterFactory: i18nSelectors.formatters.currency(state),
      language: i18nSelectors.language(state),
      partner: getSelectedPartner(state),
      partners: getStandardPartners(state),
      platform: getSelectedPlatform(state),
      platforms: getCountrySupportedPlatforms(state),
      t: translator(state),
      isKustomPartner: isKustomPartner(state),
      country: getSelectedCountryKey(state),
      annualRevenue: getSelectedAnnualRevenue(state),
    }),
    dispatch => ({
      onSelectRevenue: annualRevenue =>
        a.onChangeFormValue('annualRevenue', annualRevenue)(dispatch),
      onChangePlatform: platform => a.onChangeFormValue('platform', platform)(dispatch),
      onChangePartner: partner => a.onChangeFormValue('partner', partner)(dispatch),
    }),
  ),
  withCapabilityBeforeNext({ cap: CAPS.PRODUCTS_GET }, ({ payload }) => {
    const { platform } = payload.params;
    analytics.logEvent(ANALYTICS_EVENTS.platformSelected, { platform });
  }),
)(VolumeSeparation);
